// What the INSTALLED marti-sdk-react can actually do — probed by calling it, never read from
// source and never a version check (mirrors anna-sdk/demo/src/sdk-capabilities.ts). The probe
// is version-agnostic and cannot pass for the wrong reason: the sentinel origin is one the
// SDK's baked host map could never contain.

export type WidenedUrlBuilder = (args: {
  src: string;
  tenant: string;
  env: 'dev' | 'prod';
  route?: string;
  trace?: string;
  baseUrl?: string;
}) => string;

// RFC-2606 `.invalid`: can never resolve, so if it comes back the builder honoured `baseUrl`.
export const BASE_URL_PROBE_ORIGIN = 'https://marti-sdk-demo-probe.invalid';

const PROBE_ARGS = {
  src: 'marti-sdk-demo-probe',
  tenant: '00000000-0000-4000-8000-000000000000',
  route: '/probe',
  trace: 'probe-0000000000000000',
} as const;

export function probeBaseUrlOverride(build: WidenedUrlBuilder): boolean {
  let url: string;
  try {
    url = build({ ...PROBE_ARGS, env: 'dev', baseUrl: BASE_URL_PROBE_ORIGIN });
  } catch {
    return false;
  }
  try {
    return new URL(url).origin === BASE_URL_PROBE_ORIGIN;
  } catch {
    return false;
  }
}

export function probeLauncherOrigin(
  build: WidenedUrlBuilder,
  env: 'dev' | 'prod',
): { origin: string | null; error: string | null } {
  try {
    return { origin: new URL(build({ ...PROBE_ARGS, env })).origin, error: null };
  } catch (err) {
    return { origin: null, error: err instanceof Error ? err.message : String(err) };
  }
}

export interface SdkCapabilities {
  baseUrlOverride: boolean;
  launcherOrigin: string | null;
  launcherError: string | null;
}

export function probeSdk(build: WidenedUrlBuilder, env: 'dev' | 'prod'): SdkCapabilities {
  const launcher = probeLauncherOrigin(build, env);
  return {
    baseUrlOverride: probeBaseUrlOverride(build),
    launcherOrigin: launcher.origin,
    launcherError: launcher.error,
  };
}
